/**
 * Landing.tsx — 프리미엄 + 컬러풀 + 역동적 레이아웃
 * Apple Intelligence 스타일 mesh orb · Zig-zag feature layout
 * Bento with stacked overlap · Full-bleed CTA gradient
 */
import { useState, useEffect, useRef } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import ForceDarkTheme from '../../components/common/ForceDarkTheme'
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion'
import { Link } from 'react-router-dom'
import {
  FiMessageSquare, FiClipboard, FiCamera, FiShield,
  FiFileText, FiLayout, FiArrowRight, FiCheck,
  FiAlertTriangle, FiBookOpen, FiAward,
  FiChevronDown, FiZap, FiLock, FiGlobe, FiRefreshCw,
  FiGitBranch, FiTrello, FiDollarSign, FiShare2, FiSearch,
} from 'react-icons/fi'
import Button from '../../components/ui/Button'
import Brand, { renderWithBrand } from '../../components/brand/Brand'
import ExamplePreviewModal from '../../components/common/ExamplePreviewModal'
import OnboardingTourEmbed from '../../features/onboarding/OnboardingTourEmbed'
import { KoreaGovSymbol } from '../../components/common/GovLogos'

// ── 애니메이션 헬퍼 ─────────────────────────────────────────────
const inView = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-48px' },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
}

const stagger = (i: number) => ({
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-48px' },
  transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
})

// ── 플랜 할당량 (키만, 텍스트는 i18n) ─────────────────────────
// 수치 출처: heinrich/ops/sql/004_plan_repricing_2026_05.sql §10.2 (챗봇=일 단위, 그 외=월 단위)
const PLAN_QUOTA = {
  chatbot:         { free: 'quota.dayN:10',   starter: 'quota.dayN:60',    pro: 'quota.dayN:150',   business: 'quota.dayN:200' },
  jsa:             { free: 'quota.monthN:5',  starter: 'quota.monthN:12',  pro: 'quota.monthN:50',  business: 'quota.monthN:120' },
  tbm:             { free: 'quota.monthN:1',  starter: 'quota.monthN:5',   pro: 'quota.monthN:20',  business: 'quota.monthN:45' },
  imageAnalysis:   { free: 'quota.monthN:5',  starter: 'quota.monthN:15',  pro: 'quota.monthN:40',  business: 'quota.monthN:100' },
  corrective:      { free: 'quota.monthN:2',  starter: 'quota.monthN:10',  pro: 'quota.monthN:30',  business: 'quota.monthN:80' },
  riskAssessment:  { free: 'quota.monthN:5',  starter: 'quota.monthN:12',  pro: 'quota.monthN:40',  business: 'quota.monthN:100' },
  customTemplate:  { free: 'quota.dash',      starter: 'quota.dash',       pro: 'quota.dash',       business: 'quota.unlimited' },
} as const

type PlanQuotaKey = keyof typeof PLAN_QUOTA

// id → /features/{slug} SEO 랜딩 매핑 (cross-link)
const FEATURE_SLUG_MAP: Record<string, string> = {
  chatbot:         'chatbot',
  jsa:             'jsa',
  imageAnalysis:   'image-analysis',
  tbm:             'tbm',
  corrective:      'corrective',
  workPermit:      'work-permit',
  accidentReport:  'accident-report',
  riskAssessment:  'risk-assessment',
  safetyEducation: 'safety-education',
  eta:             'eta',
  fta:             'fta',
  safetyCost:      'safety-cost',
  pnidHazop:       'pnid-hazop',
}

function useQuotaText() {
  const { t, i18n } = useTranslation()
  return (token: string) => {
    if (token === 'quota.unlimited') return i18n.language === 'ko' ? '무제한' : 'Unlimited'
    if (token === 'quota.dash') return '—'
    const [prefix, n] = token.split(':')
    if (prefix === 'quota.dayN') return i18n.language === 'ko' ? `일 ${n}회` : `${n}/day`
    if (prefix === 'quota.monthN') return i18n.language === 'ko' ? `월 ${n}회` : `${n}/mo`
    if (prefix === 'quota.weekN') return i18n.language === 'ko' ? `주 ${n}회` : `${n}/week`
    return t(token)
  }
}

const PlanPills = ({ quotaKey }: { quotaKey?: PlanQuotaKey }) => {
  if (!quotaKey) return null
  const { t } = useTranslation()
  const quotaText = useQuotaText()
  const q = PLAN_QUOTA[quotaKey]
  const planLabels: Record<'free' | 'starter' | 'pro' | 'business', string> = {
    free: t('landing.planQuota.free'),
    starter: t('landing.planQuota.starter'),
    pro: t('landing.planQuota.pro'),
    business: t('landing.planQuota.business'),
  }
  return (
    <div className="grid grid-cols-4 gap-0 mt-auto pt-4 border-t border-white/[0.08] text-[11px]">
      {(['free','starter','pro','business'] as const).map((k) => (
        <div key={k}>
          <div className="font-medium text-zinc-500 tracking-wide uppercase mb-1 capitalize">{planLabels[k]}</div>
          <div className={`tabular-nums font-medium ${k === 'pro' ? 'text-white' : k === 'business' ? 'text-amber-400' : 'text-zinc-400'}`}>{quotaText(q[k])}</div>
        </div>
      ))}
    </div>
  )
}

// ── Zig-zag 기능 데이터 ─────────────────────────────────────────
const ZIG_FEATURES = [
  // 1. 안전교육 자료 자동 생성 (PPTX·영상·퀴즈) — 최상단 (사용자 요청)
  {
    id: 'safetyEducation',
    icon: FiBookOpen,
    quotaKey: undefined as unknown as PlanQuotaKey,
    gradient: 'from-purple-600/25 to-fuchsia-600/10',
    border: 'border-purple-500/30',
    iconColor: 'text-purple-400',
    iconBg: 'bg-purple-500/15 border-purple-500/30',
    glow: 'bg-purple-500/20',
    image: '/assets/features/safety-education.png',
    flip: false,
    examplePreviewKey: 'safety_education',
  },
  // 2. AI 챗봇
  {
    id: 'chatbot',
    icon: FiMessageSquare,
    quotaKey: 'chatbot' as PlanQuotaKey,
    gradient: 'from-amber-600/30 to-amber-600/15',
    border: 'border-amber-500/30',
    iconColor: 'text-amber-400',
    iconBg: 'bg-amber-500/15 border-amber-500/30',
    glow: 'bg-amber-500/20',
    image: '/assets/features/chatbot.png',
    flip: false,
  },
  // 3. TBM 자동 생성
  {
    id: 'tbm',
    icon: FiFileText,
    quotaKey: 'tbm' as PlanQuotaKey,
    gradient: 'from-amber-600/25 to-orange-600/10',
    border: 'border-amber-500/30',
    iconColor: 'text-amber-400',
    iconBg: 'bg-amber-500/15 border-amber-500/30',
    glow: 'bg-amber-500/20',
    image: '/assets/results/tbm-result.png',
    flip: true,
  },
  {
    id: 'jsa',
    icon: FiClipboard,
    quotaKey: 'jsa' as PlanQuotaKey,
    gradient: 'from-violet-600/25 to-purple-600/10',
    border: 'border-violet-500/30',
    iconColor: 'text-violet-400',
    iconBg: 'bg-violet-500/15 border-violet-500/30',
    glow: 'bg-violet-500/20',
    image: '/assets/results/jsa-excel.png',
    flip: true,
  },
  {
    id: 'imageAnalysis',
    icon: FiCamera,
    quotaKey: 'imageAnalysis' as PlanQuotaKey,
    gradient: 'from-emerald-600/25 to-teal-600/10',
    border: 'border-emerald-500/30',
    iconColor: 'text-emerald-400',
    iconBg: 'bg-emerald-500/15 border-emerald-500/30',
    glow: 'bg-emerald-500/20',
    image: '/assets/results/image-analysis.png',
    flip: false,
  },
  {
    id: 'corrective',
    icon: FiShield,
    quotaKey: 'corrective' as PlanQuotaKey,
    gradient: 'from-rose-600/25 to-pink-600/10',
    border: 'border-rose-500/30',
    iconColor: 'text-rose-400',
    iconBg: 'bg-rose-500/15 border-rose-500/30',
    glow: 'bg-rose-500/20',
    image: '/assets/features/corrective.png',
    flip: false,
  },
  // ── 신규 기능 (2026-04 추가) ────────────────────────────────────────
  {
    id: 'workPermit',
    icon: FiFileText,
    quotaKey: undefined as unknown as PlanQuotaKey,    // 별도 쿼터 — 카드에서 미표시
    gradient: 'from-orange-600/25 to-red-600/10',
    border: 'border-orange-500/30',
    iconColor: 'text-orange-400',
    iconBg: 'bg-orange-500/15 border-orange-500/30',
    glow: 'bg-orange-500/20',
    image: '/assets/features/work-permit.png',
    flip: true,
    examplePreviewKey: 'work_permit',
  },
  {
    id: 'accidentReport',
    icon: FiAlertTriangle,
    quotaKey: undefined as unknown as PlanQuotaKey,
    gradient: 'from-red-600/25 to-rose-600/10',
    border: 'border-red-500/30',
    iconColor: 'text-red-400',
    iconBg: 'bg-red-500/15 border-red-500/30',
    glow: 'bg-red-500/20',
    image: '/assets/features/accident-report.png',
    flip: false,
    examplePreviewKey: 'accident_report',
  },
  {
    id: 'riskAssessment',
    icon: FiShield,
    quotaKey: 'riskAssessment' as PlanQuotaKey,
    gradient: 'from-indigo-600/25 to-amber-600/10',
    border: 'border-indigo-500/30',
    iconColor: 'text-indigo-400',
    iconBg: 'bg-indigo-500/15 border-indigo-500/30',
    glow: 'bg-indigo-500/20',
    image: '/assets/features/risk-assessment.png',
    flip: true,
    examplePreviewKey: 'risk_assessment',
  },
  // ── 신규 기능 (2026-05 추가) — 고급 분석 ─────────────────────────
  {
    id: 'eta',
    icon: FiGitBranch,
    quotaKey: undefined as unknown as PlanQuotaKey,
    gradient: 'from-amber-600/25 to-orange-600/10',
    border: 'border-amber-500/30',
    iconColor: 'text-amber-400',
    iconBg: 'bg-amber-500/15 border-amber-500/30',
    glow: 'bg-amber-500/20',
    image: '/assets/features/eta.png',
    flip: true,
    examplePreviewKey: 'eta',
  },
  {
    id: 'fta',
    icon: FiTrello,
    quotaKey: undefined as unknown as PlanQuotaKey,
    gradient: 'from-rose-600/25 to-red-600/10',
    border: 'border-rose-500/30',
    iconColor: 'text-rose-400',
    iconBg: 'bg-rose-500/15 border-rose-500/30',
    glow: 'bg-rose-500/20',
    image: '/assets/features/fta.png',
    flip: false,
    examplePreviewKey: 'fta',
  },
  {
    id: 'safetyCost',
    icon: FiDollarSign,
    quotaKey: undefined as unknown as PlanQuotaKey,
    gradient: 'from-emerald-600/25 to-teal-600/10',
    border: 'border-emerald-500/30',
    iconColor: 'text-emerald-400',
    iconBg: 'bg-emerald-500/15 border-emerald-500/30',
    glow: 'bg-emerald-500/20',
    image: '/assets/features/safety-cost.png',
    flip: true,
    examplePreviewKey: 'safety_cost',
  },
  {
    id: 'pnidHazop',
    icon: FiShare2,
    quotaKey: undefined as unknown as PlanQuotaKey,
    gradient: 'from-purple-600/25 to-cyan-600/10',
    border: 'border-purple-500/30',
    iconColor: 'text-purple-400',
    iconBg: 'bg-purple-500/15 border-purple-500/30',
    glow: 'bg-purple-500/20',
    image: '/assets/features/pnid-hazop.png',
    flip: false,
  },
  {
    id: 'customTemplate',
    icon: FiLayout,
    quotaKey: 'customTemplate' as PlanQuotaKey,
    gradient: 'from-amber-600/25 to-amber-600/10',
    border: 'border-amber-500/30',
    iconColor: 'text-amber-400',
    iconBg: 'bg-amber-500/15 border-amber-500/30',
    glow: 'bg-amber-500/20',
    image: '/assets/features/custom-template.png',
    flip: true,
  },
] as const

// ── 가격 카드 ─────────────────────────────────────────────────────
const PRICE_PLANS = [
  {
    id: 'free',
    nameKey: 'landing.planQuota.free',
    name: 'Free',
    price: '₩0',
    period: '/월',
    descKey: 'landing.pricingPreview.plans.freeDesc',
    ctaKey: 'landing.pricingPreview.plans.freeCta',
    href: '/signup',
    gradient: 'from-zinc-700/30 to-zinc-800/20',
    border: 'border-zinc-600/30',
    featureKeys: [
      'landing.pricingPreview.plans.freeFeature1',
      'landing.pricingPreview.plans.freeFeature2',
      'landing.pricingPreview.plans.freeFeature3',
    ],
    scale: '',
  },
  {
    id: 'pro',
    name: 'Professional',
    price: '₩30,000',
    period: '/월',
    descKey: 'landing.pricingPreview.plans.proDesc',
    ctaKey: 'landing.pricingPreview.plans.proCta',
    href: '/signup?plan=pro',
    gradient: 'from-amber-600/35 to-amber-600/20',
    border: 'border-amber-500/50',
    featureKeys: [
      'landing.pricingPreview.plans.proFeature1',
      'landing.pricingPreview.plans.proFeature2',
      'landing.pricingPreview.plans.proFeature3',
      'landing.pricingPreview.plans.proFeature4',
    ],
    scale: 'scale-[1.04] z-10',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    priceKey: 'landing.pricingPreview.plans.enterprisePrice',
    period: '',
    descKey: 'landing.pricingPreview.plans.enterpriseDesc',
    ctaKey: 'landing.pricingPreview.plans.enterpriseCta',
    href: '/contact',
    gradient: 'from-violet-600/30 to-purple-600/15',
    border: 'border-violet-500/30',
    featureKeys: [
      'landing.pricingPreview.plans.enterpriseFeature1',
      'landing.pricingPreview.plans.enterpriseFeature2',
      'landing.pricingPreview.plans.enterpriseFeature3',
      'landing.pricingPreview.plans.enterpriseFeature4',
    ],
    scale: '',
  },
] as const

function useTypingEffect(text: string, speed = 18, startDelay = 0) {
  const [displayed, setDisplayed] = useState('')
  const [done, setDone] = useState(false)

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>
    let i = 0
    setDisplayed('')
    setDone(false)
    timeout = setTimeout(() => {
      const interval = setInterval(() => {
        i++
        setDisplayed(text.slice(0, i))
        if (i >= text.length) { clearInterval(interval); setDone(true) }
      }, speed)
      return () => clearInterval(interval)
    }, startDelay)
    return () => clearTimeout(timeout)
  }, [text, speed, startDelay])

  return { displayed, done }
}

function DemoPreview() {
  const { t } = useTranslation()
  const [started, setStarted] = useState(false)
  const [showRefs, setShowRefs] = useState(false)
  const aiText = t('landing.demo.aiMessage')
  const { displayed, done } = useTypingEffect(aiText, 14, started ? 800 : 9999999)

  useEffect(() => {
    if (done) {
      const t2 = setTimeout(() => setShowRefs(true), 300)
      return () => clearTimeout(t2)
    }
  }, [done])

  const refs = [
    { id: 'AC-2023-1847', titleKey: 'landing.demo.ref1Title', year: '2023' },
    { id: 'AC-2022-0934', titleKey: 'landing.demo.ref2Title', year: '2022' },
    { id: 'AC-2024-0211', titleKey: 'landing.demo.ref3Title', year: '2024' },
  ]

  return (
    <div className="relative rounded-lg border border-amber-500/20 bg-gradient-to-br from-zinc-900 to-zinc-950 overflow-hidden shadow-md shadow-amber-500/10">
      <div className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.08] bg-zinc-900/80">
        <span className="h-2.5 w-2.5 rounded-full bg-rose-500/70" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-500/70" />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-500/70" />
        <span className="ml-3 text-xs text-zinc-500 font-mono">{t('landing.demo.terminalTitle')}</span>
        <span className="ml-auto flex items-center gap-1.5 text-[10px] text-emerald-400 font-mono">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
          {t('landing.demo.online')}
        </span>
      </div>

      <div className="p-5 flex flex-col gap-4 min-h-[320px]">
        <div className="flex justify-end">
          <div className="max-w-[80%] px-4 py-2.5 rounded-lg rounded-tr-sm bg-gradient-to-br from-amber-500/20 to-amber-500/10 border border-amber-500/25 text-sm text-zinc-200 leading-relaxed">
            {t('landing.demo.userMessage')}
          </div>
        </div>

        <div className="flex items-start gap-3">
          <div className="h-8 w-8 rounded-md bg-gradient-to-br from-amber-500/30 to-amber-500/20 border border-amber-500/40 flex items-center justify-center shrink-0 text-amber-400 text-xs font-mono font-bold mt-0.5">
            AI
          </div>
          <div className="flex-1">
            {!started ? (
              <button
                onClick={() => setStarted(true)}
                className="px-4 py-2 rounded-md text-sm font-medium bg-gradient-to-r from-amber-500/20 to-amber-500/10 border border-amber-500/30 text-amber-300 hover:from-amber-500/30 hover:to-amber-500/20 transition-all duration-200"
              >
                <FiArrowRight size={12} className="inline mr-1.5" /> {t('landing.demo.startAnswer')}
              </button>
            ) : (
              <div className="px-4 py-3 rounded-lg rounded-tl-sm bg-white/[0.04] border border-white/[0.08] text-sm text-zinc-300 leading-relaxed whitespace-pre-line">
                {displayed}
                {!done && (
                  <span className="inline-block w-0.5 h-3.5 bg-amber-400 animate-pulse ml-0.5 align-middle" />
                )}
              </div>
            )}

            <AnimatePresence>
              {showRefs && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  className="mt-3 flex flex-col gap-2"
                >
                  <p className="text-xs text-zinc-500 pl-1">{t('landing.demo.referenceCases')}</p>
                  {refs.map((ref, i) => (
                    <motion.div
                      key={ref.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.1 }}
                      className="px-3 py-2 rounded-md bg-gradient-to-r from-amber-500/08 to-transparent border border-amber-500/15 flex items-center gap-3"
                    >
                      <span className="font-mono text-[10px] text-amber-400 shrink-0">{ref.id}</span>
                      <span className="text-xs text-zinc-400 flex-1">{t(ref.titleKey)}</span>
                      <span className="text-[10px] text-zinc-500">{ref.year}</span>
                    </motion.div>
                  ))}
                  <div className="mt-1 pl-1">
                    <Link
                      to="/signup"
                      className="text-xs text-amber-400 hover:text-amber-300 transition-colors inline-flex items-center gap-1"
                    >
                      {t('landing.demo.tryMore')} <FiArrowRight size={11} aria-hidden="true" />
                    </Link>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── 메인 컴포넌트 ────────────────────────────────────────────────
export default function Landing() {
  const { t } = useTranslation()
  const [openFaq, setOpenFaq] = useState<number | null>(null)
  const [exampleKey, setExampleKey] = useState<string | null>(null)
  const heroRef = useRef<HTMLDivElement>(null)
  const { scrollY } = useScroll()
  const heroY = useTransform(scrollY, [0, 600], [0, 120])

  const handleFeaturesScroll = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })
  }

  const faqs = [1, 2, 3, 4, 5, 6].map((n) => ({
    q: t(`landing.faq.q${n}`),
    a: t(`landing.faq.a${n}`),
  }))

  const stats = [
    { value: t('landing.stats.casesValue'), label: t('landing.stats.cases') },
    { value: t('landing.stats.autoUpdateValue'), label: t('landing.stats.autoUpdate') },
    { value: t('landing.stats.lawUpdateValue'), label: t('landing.stats.lawUpdate') },
    { value: t('landing.stats.jsaAutoValue'), label: t('landing.stats.jsaAuto') },
  ]

  return (
    <>
      <ForceDarkTheme />
      <Helmet>
        <title>{t('landing.pageTitle')}</title>
        <meta name="description" content={t('landing.metaDescription')} />
        <link rel="canonical" href="https://heinrich.dasancms.co.kr/" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Heinrich" />
        <meta property="og:locale" content="ko_KR" />
        <meta property="og:url" content="https://heinrich.dasancms.co.kr/" />
        <meta property="og:title" content={t('landing.ogTitle')} />
        <meta property="og:description" content={t('landing.ogDescription')} />
        <meta property="og:image" content="https://heinrich.dasancms.co.kr/og/default.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Heinrich — 산업안전 AI SaaS" />
        <meta property="og:locale:alternate" content="en_US" />
        <link rel="alternate" hrefLang="ko" href="https://heinrich.dasancms.co.kr/" />
        <link rel="alternate" hrefLang="en" href="https://heinrich.dasancms.co.kr/" />
        <link rel="alternate" hrefLang="x-default" href="https://heinrich.dasancms.co.kr/" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('landing.ogTitle')} />
        <meta name="twitter:description" content={t('landing.ogDescription')} />
        <meta name="twitter:image" content="https://heinrich.dasancms.co.kr/og/default.png" />
      </Helmet>

      {/* A. HERO */}
      <section
        ref={heroRef}
        className="noise-overlay relative overflow-hidden bg-zinc-950 min-h-screen flex items-center"
        aria-label={t('landing.heroAriaLabel')}
      >
        {/* 배경 사진 + 다크 오버레이 — 산업 컨트롤룸 다큐멘터리 톤 */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <img
            src="/assets/hero/landing-hero-ambient.jpg"
            alt=""
            className="w-full h-full object-cover object-center opacity-40"
            loading="eager"
            fetchPriority="high"
          />
          {/* 좌측에서 우측으로 다크 그라디언트 (텍스트 가독성 보장) */}
          <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/85 to-zinc-950/40" />
          {/* 하단 페이드 */}
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-zinc-950 to-transparent" />
        </div>
        {/* 하단 그리드 라인 */}
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-white/[0.10] to-transparent pointer-events-none z-10" aria-hidden="true" />

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 py-32 pt-40 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-[7fr_5fr] gap-12 lg:gap-16 items-center">
            <div>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mb-7 flex flex-wrap items-center gap-2"
              >
                <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-medium bg-white/[0.04] border border-white/[0.10] text-zinc-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" aria-hidden="true" />
                  {t('landing.heroBadge')}
                </span>
                {/* 정부 수상 배지 — 클릭 시 About 수상 섹션 앵커 이동 */}
                <Link
                  to="/about#awards"
                  className="group inline-flex items-center gap-2.5 pl-1.5 pr-4 py-1.5 rounded-full bg-gradient-to-r from-amber-500/[0.14] via-amber-500/[0.08] to-transparent border border-amber-500/30 hover:border-amber-400/60 transition-colors"
                  aria-label="고용노동부 장관상 수상 — 수상 이력 보기"
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-zinc-950/70 border border-amber-400/30">
                    <KoreaGovSymbol className="w-4 h-4" />
                  </span>
                  <span className="flex flex-col leading-tight text-left">
                    <span className="text-[9.5px] font-semibold tracking-[0.18em] text-amber-400/70 uppercase">Ministry of Employment and Labor</span>
                    <span className="text-[13px] font-bold text-amber-300 flex items-center gap-1.5">
                      고용노동부 장관상 수상
                      <FiArrowRight size={12} className="opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" aria-hidden />
                    </span>
                  </span>
                </Link>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                className="font-bold tracking-tight leading-[1.05] mb-7"
                style={{ fontSize: 'clamp(2.75rem, 6.5vw, 5rem)' }}
              >
                <span className="text-white">{t('landing.heroHeadline1')}</span>
                <br />
                <span className="text-white">{t('landing.heroHeadline2')}</span>
                <br />
                <span className="text-gradient-hero bg-gradient-to-r from-amber-400 to-amber-300 bg-clip-text text-transparent">
                  {t('landing.heroHeadline3')}
                </span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.35 }}
                className="text-lg lg:text-xl text-zinc-400 mb-10 max-w-xl leading-relaxed"
              >
                {t('landing.heroDescription')}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="flex flex-col sm:flex-row gap-3 items-start"
              >
                <Link to="/signup" aria-label={t('landing.heroCtaAria')}>
                  <Button size="lg" rightIcon={<FiArrowRight aria-hidden="true" />} className="text-base px-8">
                    {t('landing.heroCta')}
                  </Button>
                </Link>
                <Link
                  to="/ac-datacenter"
                  aria-label={t('landing.heroExploreCtaAria', { defaultValue: '사고사례 탐색기 열기' })}
                  className="group relative inline-flex items-center gap-2 px-7 py-3.5 rounded-md text-base font-semibold text-amber-100 border border-amber-400/40 hover:border-amber-300/70 bg-amber-400/[0.07] hover:bg-amber-400/[0.14] transition-all duration-200 shadow-[0_0_22px_-8px_rgba(251,191,36,0.65)]"
                >
                  <FiSearch aria-hidden="true" className="opacity-80" />
                  {t('landing.heroExploreCta', { defaultValue: '사고사례 탐색' })}
                  <span
                    aria-label="New"
                    className="inline-flex items-center justify-center leading-none text-[9px] font-extrabold tracking-[0.05em] px-1.5 py-0.5 rounded-md bg-red-500 text-white uppercase ring-1 ring-red-400/50 pointer-events-none"
                  >
                    NEW
                  </span>
                </Link>
                <a
                  href="#features"
                  onClick={handleFeaturesScroll}
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md text-base font-medium text-zinc-300 border border-white/[0.12] hover:border-white/[0.24] hover:text-white bg-transparent hover:bg-white/[0.06] transition-all duration-200"
                  aria-label={t('landing.heroSecondCtaAria')}
                >
                  {t('landing.heroSecondCta')}
                </a>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 0.8 }}
                className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6"
              >
                {stats.map((stat) => (
                  <div key={stat.label} className="text-center sm:text-left">
                    <div
                      className="font-semibold text-white tabular-nums mb-1"
                      style={{ fontSize: 'clamp(1.1rem, 2.5vw, 1.5rem)' }}
                    >
                      {stat.value}
                    </div>
                    <div className="text-xs text-zinc-500 tracking-wide">{stat.label}</div>
                  </div>
                ))}
              </motion.div>
            </div>

            <div className="relative hidden lg:block">
              <DemoPreview />
            </div>
          </div>
        </div>

        <motion.a
          href="#features"
          onClick={handleFeaturesScroll}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-zinc-500 hover:text-zinc-300 transition-colors"
          aria-label={t('landing.scrollHint')}
        >
          <span className="text-[10px] tracking-widest uppercase">{t('landing.scrollLabel')}</span>
          <FiChevronDown size={20} className="animate-bounce" aria-hidden="true" />
        </motion.a>
      </section>

      {/* A-a. 데모 투어 임베드 — Hero 직후(가입 없이 실제 화면 체험) */}
      <OnboardingTourEmbed scrollId="onb-landing-scroll" />

      {/* A-b. Why Heinrich — 이름의 유산 (Hero 직후) */}
      <section className="bg-gradient-to-b from-zinc-950 to-zinc-900 border-y border-white/[0.06]">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-20 lg:py-24">
          <motion.div {...inView} className="text-center mb-10">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-amber-500/15 to-orange-500/10 border border-amber-500/25 text-amber-300 uppercase tracking-widest mb-5">
              {t('whyHeinrich.badge')} <Brand className="normal-case tracking-normal" />
            </span>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mt-2 mb-4 leading-tight">
              {renderWithBrand(t('whyHeinrich.title'))}
            </h2>
          </motion.div>

          {/* 좌(이미지) 우(텍스트) 에디토리얼 — 100년 유산의 시각적 무게 */}
          <div className="grid md:grid-cols-[5fr_6fr] gap-8 lg:gap-12 items-center mb-12">
            <div className="relative aspect-[4/3] md:aspect-auto md:h-full rounded-md overflow-hidden border border-white/[0.08]">
              <img
                src="/assets/section/landing-heritage.jpg"
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/40 via-transparent to-transparent" />
            </div>
            <div className="space-y-5">
              <p className="text-zinc-300 text-base md:text-lg leading-[1.85]">
                {renderWithBrand(t('whyHeinrich.heritage1'))}
              </p>
              <p className="text-zinc-100 text-base md:text-lg leading-[1.85] font-medium">
                {renderWithBrand(t('whyHeinrich.heritage3'))}
              </p>
            </div>
          </div>

          {/* 숫자 비교 배지 4개 */}
          <motion.div {...inView} className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
            {[
              { label: t('whyHeinrich.statHeinrich'), value: '75,000',  accent: false },
              { label: t('whyHeinrich.statFacts'),    value: '24,000',  accent: false },
              { label: t('whyHeinrich.statAcademic'), value: '~20,000', accent: false },
              { label: t('whyHeinrich.statOurs'),     value: '400,000+', accent: true },
            ].map((s) => (
              <div
                key={s.label}
                className={`rounded-md border p-4 text-center ${
                  s.accent
                    ? 'border-amber-500/40 bg-amber-500/5'
                    : 'border-white/[0.08] bg-white/[0.02]'
                }`}
              >
                <p className={`text-2xl md:text-3xl font-extrabold mb-1 ${s.accent ? 'text-amber-400' : 'text-zinc-300'}`}>
                  {s.value}
                </p>
                <p className="text-[10px] md:text-xs text-zinc-500 leading-snug">{renderWithBrand(s.label)}</p>
              </div>
            ))}
          </motion.div>

          {/* 대한민국 정부 3개 부처 수상 실적 — 상패(플라크) 3장. 장관상이 주인공(가운데·금장) */}
          <motion.div {...inView} className="mb-12">
            <div className="text-center mb-8">
              <span className="inline-flex items-center gap-3 text-[11px] font-semibold tracking-[0.3em] uppercase text-amber-400/90">
                <span className="h-px w-8 bg-gradient-to-r from-transparent to-amber-400/50" aria-hidden />
                Government Recognized
                <span className="h-px w-8 bg-gradient-to-l from-transparent to-amber-400/50" aria-hidden />
              </span>
              <p className="text-sm text-zinc-500 mt-2.5">대한민국 정부 3개 부처 공모전 · 제품 및 서비스 개발 부문 수상</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 max-w-4xl mx-auto md:items-stretch">
              {([
                { ministry: '행정안전부', tier: '우수상', year: '2025', note: '재난안전 데이터 활용 검증', img: 'medal_silver.webp',
                  grad: 'from-sky-400/40 via-sky-500/10 to-transparent', chip: 'bg-sky-400/10 text-sky-300 border-sky-400/25', glow: 'bg-sky-400/15', main: false },
                { ministry: '고용노동부', tier: '장관상', year: '2026', note: '산업안전보건법·중대재해처벌법 주무 부처', img: 'medal_gold.webp',
                  grad: 'from-amber-300/70 via-amber-500/15 to-amber-500/5', chip: 'bg-gradient-to-b from-amber-400/25 to-amber-500/10 text-amber-300 border-amber-400/40', glow: 'bg-amber-400/25', main: true },
                { ministry: '기후부', tier: '장려상', year: '2026', note: 'AX 인공지능 전환 혁신 인정', img: 'medal_bronze.webp',
                  grad: 'from-emerald-400/40 via-emerald-500/10 to-transparent', chip: 'bg-emerald-400/10 text-emerald-300 border-emerald-400/25', glow: 'bg-emerald-400/15', main: false },
              ] as const).map((a) => (
                <div key={a.ministry}
                  className={`relative rounded-2xl p-[1px] bg-gradient-to-b ${a.grad} ${a.main ? 'order-first md:order-none md:scale-[1.05] md:z-10' : 'md:self-center'}`}>
                  <div className="relative h-full rounded-2xl bg-zinc-950 px-6 py-7 overflow-hidden text-center">
                    {/* 엠블럼 후광 + 메달 워터마크 */}
                    <div className={`absolute -top-12 left-1/2 -translate-x-1/2 h-28 w-44 rounded-full blur-3xl ${a.glow}`} aria-hidden />
                    <FiAward className="absolute -right-5 -bottom-5 w-24 h-24 text-white/[0.03]" aria-hidden />
                    <div className="relative">
                      <img src={`/assets/awards/${a.img}`} alt="" loading="lazy" aria-hidden
                        className={`mx-auto -mt-2 object-cover [mask-image:radial-gradient(circle,black_52%,transparent_73%)] ${a.main ? 'w-40 h-40 mb-0' : 'w-28 h-28 mb-1'}`} />
                      <div className="text-[10.5px] font-medium tracking-[0.12em] text-zinc-500">대한민국 정부 · {a.year}</div>
                      <div className={`font-bold text-white mt-1 ${a.main ? 'text-xl' : 'text-base'}`}>{a.ministry}</div>
                      <span className={`inline-flex mt-3.5 px-4 py-1.5 rounded-full border font-bold tracking-wide ${a.chip} ${a.main ? 'text-base' : 'text-sm'}`}>
                        {a.tier}
                      </span>
                      <div className="text-[11px] text-zinc-500 mt-3.5 leading-snug">{a.note}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-7">
              <Link
                to="/about"
                className="inline-flex items-center gap-2 text-xs font-semibold px-4 py-2.5 rounded-full bg-white/[0.03] border border-white/[0.10] text-zinc-300 hover:border-amber-400/40 hover:text-amber-300 transition-colors"
              >
                수상 이력 자세히 보기
                <FiArrowRight size={13} />
              </Link>
            </div>
          </motion.div>

          <motion.div {...inView} className="text-center">
            <Link
              to="/about"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-semibold text-zinc-900 dark:text-white border border-amber-500/30 hover:bg-amber-500/10 hover:border-amber-500/50 transition-all"
            >
              {t('whyHeinrich.ctaLearnMore')}
              <FiArrowRight size={14} aria-hidden="true" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* A-c. 풀블리드 에디토리얼 디바이더 — 현장 작업 분위기 (Why ↔ Features 사이) */}
      <section className="relative h-[260px] md:h-[340px] overflow-hidden bg-zinc-950" aria-hidden="true">
        <img
          src="/assets/section/landing-site-work.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-950 via-transparent to-zinc-950" />
        <div className="absolute inset-0 bg-zinc-950/30" />
      </section>

      {/* B. 기능 */}
      <section id="features" className="bg-zinc-950" aria-labelledby="features-title">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-24 lg:pt-32 pb-16">
          <motion.div {...inView} className="text-center">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-amber-500/15 to-amber-500/10 border border-amber-500/25 text-amber-300 uppercase tracking-widest mb-5">
              {t('landing.featuresBadge')}
            </span>
            <h2
              id="features-title"
              className="font-bold tracking-tight leading-tight mb-5"
              style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)' }}
            >
              <span className="text-white">{t('landing.featuresHeadline1')}</span>
              <br />
              <span className="text-white">
                {t('landing.featuresHeadline2')}
              </span>
            </h2>
            <p className="text-[15px] leading-[1.7] text-zinc-400 max-w-2xl mx-auto">
              {t('landing.featuresDescription')}
            </p>
          </motion.div>
        </div>

        <div className="flex flex-col">
          {ZIG_FEATURES.map((feat, i) => {
            const title = t(`landing.zigFeatures.${feat.id}.title`)
            const desc = t(`landing.zigFeatures.${feat.id}.desc`)
            const bullets = [
              t(`landing.zigFeatures.${feat.id}.bullet1`),
              t(`landing.zigFeatures.${feat.id}.bullet2`),
              t(`landing.zigFeatures.${feat.id}.bullet3`),
            ]
            // 지그재그 레이아웃은 순서에 의존 → 인덱스로 계산 (카드 재정렬에도 정상 교차)
            const flip = i % 2 === 1
            return (
            <div
              key={feat.id}
              className="relative overflow-hidden"
            >
              <div
                className={`absolute ${flip ? 'right-0' : 'left-0'} top-1/2 -translate-y-1/2 w-[40rem] h-[40rem] ${feat.glow} rounded-full blur-3xl opacity-25 pointer-events-none`}
                aria-hidden="true"
              />

              <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-24 relative z-10">
                <motion.div
                  {...stagger(0)}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center ${flip ? 'lg:direction-rtl' : ''}`}
                  style={{ direction: 'ltr' }}
                >
                  <div className={flip ? 'lg:order-2' : 'lg:order-1'}>
                    <div className={`inline-flex h-12 w-12 items-center justify-center rounded-lg ${feat.iconBg} border mb-6`}>
                      <feat.icon size={22} className={feat.iconColor} aria-hidden="true" />
                    </div>

                    <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4 tracking-tight">
                      {title}
                    </h3>
                    <p className="text-[15px] leading-[1.7] text-zinc-400 mb-6">
                      {desc}
                    </p>

                    <ul className="flex flex-col gap-3 mb-8">
                      {bullets.map((b) => (
                        <li key={b} className="flex items-start gap-3 text-sm text-zinc-300">
                          <FiCheck size={14} className={`${feat.iconColor} shrink-0 mt-0.5`} aria-hidden="true" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="flex flex-wrap items-center gap-2 mb-4">
                      {('examplePreviewKey' in feat) && feat.examplePreviewKey && (
                        <button
                          onClick={() => setExampleKey(feat.examplePreviewKey)}
                          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm border ${feat.border} ${feat.iconColor} hover:bg-white/5 transition`}
                        >
                          <FiAward size={14} /> 예시 결과물 미리보기
                        </button>
                      )}
                      {FEATURE_SLUG_MAP[feat.id] && (
                        <Link
                          to={`/features/${FEATURE_SLUG_MAP[feat.id]}`}
                          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm border ${feat.border} ${feat.iconColor} hover:bg-white/5 transition`}
                        >
                          {title} 자세히 보기 <FiArrowRight size={14} />
                        </Link>
                      )}
                    </div>

                    <PlanPills quotaKey={feat.quotaKey} />
                  </div>

                  <div className={flip ? 'lg:order-1' : 'lg:order-2'}>
                    <motion.div
                      whileInView={{ opacity: 1, y: 0 }}
                      initial={{ opacity: 0, y: 24 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.7, delay: 0.2 }}
                    >
                      <div className={`relative rounded-lg border ${feat.border} bg-gradient-to-br ${feat.gradient} overflow-hidden shadow-md`}>
                        <div className={`absolute inset-0 rounded-lg bg-gradient-to-br ${feat.gradient} opacity-50`} aria-hidden="true" />
                        <img
                          src={feat.image}
                          alt={t('landing.zigFeatures.imageAlt', { title })}
                          className="relative z-10 w-full h-72 object-cover object-top rounded-lg"
                          onError={(e) => {
                            // 이미지 자산 없으면 아이콘 placeholder 로 fallback
                            const img = e.currentTarget
                            img.style.display = 'none'
                            const ph = img.nextElementSibling as HTMLElement | null
                            if (ph) ph.style.display = 'flex'
                          }}
                        />
                        <div
                          className="relative z-10 w-full h-72 rounded-lg items-center justify-center"
                          style={{ display: 'none' }}
                        >
                          <feat.icon size={96} className={`${feat.iconColor} opacity-50`} />
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/60 via-transparent to-transparent rounded-lg z-20" aria-hidden="true" />
                        <div className="absolute top-4 left-4 z-30">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-semibold bg-black/50 backdrop-blur-sm border ${feat.border} ${feat.iconColor}`}>
                            {t('landing.liveOutput')}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </motion.div>
              </div>

              {i < ZIG_FEATURES.length - 1 && (
                <div className="h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" aria-hidden="true" />
              )}
            </div>
            )
          })}
        </div>
      </section>

      {/* C. AI 콘텐츠 자동화 — TBM 기반 자동 생성 */}
      <section
        className="py-24 lg:py-32 relative overflow-hidden bg-zinc-950"
        aria-labelledby="ai-content-title"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <motion.div {...inView} className="text-center mb-16">
            <span className="inline-block text-xs font-mono text-zinc-500 tracking-wider mb-5">
              {t('landing.aiContent.badge')}
            </span>
            <h2
              id="ai-content-title"
              className="font-bold tracking-tight leading-tight mb-5 text-white"
              style={{ fontSize: 'clamp(1.75rem, 4.5vw, 3rem)' }}
            >
              {t('landing.aiContent.headline1')}{' '}
              {t('landing.aiContent.headline2')}
            </h2>
            <p className="text-[15px] leading-[1.75] text-zinc-400 max-w-2xl mx-auto">
              {t('landing.aiContent.description')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <motion.div {...stagger(1)} className="relative">
              <div className="relative rounded-lg border border-white/[0.08] overflow-hidden bg-zinc-900 hover:border-white/[0.18] transition-colors">
                <img src="/assets/ai-jsa-poster.png" alt={t('landing.aiContent.posterAlt')} className="w-full h-80 lg:h-96 object-cover object-center" />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/80 via-zinc-950/10 to-transparent" aria-hidden="true" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <span className="inline-block px-2.5 py-1 rounded-md text-[10px] font-semibold bg-white/[0.10] text-white border border-white/[0.20] backdrop-blur-sm mb-2">
                    {t('landing.aiContent.poster')}
                  </span>
                  <p className="text-xs text-white leading-snug">{t('landing.aiContent.posterDesc')}</p>
                </div>
              </div>
            </motion.div>

            <motion.div {...stagger(2)} className="relative">
              <div className="relative rounded-lg border border-white/[0.08] overflow-hidden bg-zinc-900 hover:border-white/[0.18] transition-colors">
                <img src="/assets/ai-jsa-comic.png" alt={t('landing.aiContent.comicAlt')} className="w-full h-80 lg:h-96 object-cover object-center" />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/80 via-zinc-950/10 to-transparent" aria-hidden="true" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <span className="inline-block px-2.5 py-1 rounded-md text-[10px] font-semibold bg-white/[0.10] text-white border border-white/[0.20] backdrop-blur-sm mb-2">
                    {t('landing.aiContent.comic')}
                  </span>
                  <p className="text-xs text-white leading-snug">{t('landing.aiContent.comicDesc')}</p>
                </div>
              </div>
            </motion.div>
          </div>

          <motion.div {...inView} className="text-center mt-14">
            <Link to="/dashboard/tbm">
              <Button size="md" rightIcon={<FiArrowRight aria-hidden="true" />}>
                {t('landing.aiContent.tbmCta')}
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <ResultsTabSection />

      {/* D. Why Heinrich */}
      <section
        className="py-24 lg:py-32 relative overflow-hidden bg-[#07091300] bg-[#060a14]"
        aria-labelledby="why-title"
      >
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute top-1/2 left-0 w-[45rem] h-[45rem] bg-amber-500/06 rounded-full blur-3xl -translate-y-1/2" />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <motion.div {...inView} className="text-center mb-16">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-amber-500/15 border border-amber-500/25 text-zinc-900 dark:text-white uppercase tracking-widest mb-5">
              {t('landing.why.badge')}
            </span>
            <h2
              id="why-title"
              className="font-bold tracking-tight leading-tight mb-5"
              style={{ fontSize: 'clamp(1.75rem, 4.5vw, 3rem)' }}
            >
              <span className="text-white">{renderWithBrand(t('landing.why.headline'))}</span>
            </h2>
            <p className="text-[15px] leading-[1.7] text-zinc-400 max-w-xl mx-auto">
              {renderWithBrand(t('landing.why.description'))}
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto grid grid-cols-1 gap-5">
            {[
              {
                icon: FiRefreshCw,
                label: t('landing.why.lawUpdateLabel'),
                saife: { text: t('landing.why.lawUpdateHeinrich'), bar: 95 },
                competitor: { text: t('landing.why.lawUpdateCompetitor'), bar: 20 },
                saifeColor: 'from-amber-500 to-amber-500',
                compColor: 'from-zinc-600 to-zinc-700',
              },
              {
                icon: FiMessageSquare,
                label: t('landing.why.casesLabel'),
                saife: { text: t('landing.why.casesHeinrich'), bar: 90 },
                competitor: { text: t('landing.why.casesCompetitor'), bar: 15 },
                saifeColor: 'from-violet-500 to-purple-500',
                compColor: 'from-zinc-600 to-zinc-700',
              },
              {
                icon: FiLock,
                label: t('landing.why.securityLabel'),
                saife: { text: t('landing.why.securityHeinrich'), bar: 100 },
                competitor: { text: t('landing.why.securityCompetitor'), bar: 10 },
                saifeColor: 'from-emerald-500 to-teal-500',
                compColor: 'from-zinc-600 to-zinc-700',
              },
              {
                icon: FiGlobe,
                label: t('landing.why.langLabel'),
                saife: { text: t('landing.why.langHeinrich'), bar: 85 },
                competitor: { text: t('landing.why.langCompetitor'), bar: 30 },
                saifeColor: 'from-amber-500 to-orange-500',
                compColor: 'from-zinc-600 to-zinc-700',
              },
            ].map((row, i) => (
              <motion.div
                key={row.label}
                {...stagger(i)}
                className="rounded-lg border border-white/[0.08] bg-gradient-to-br from-zinc-900/80 to-zinc-950/80 p-6 backdrop-blur-sm"
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="h-9 w-9 rounded-md bg-white/[0.06] border border-white/[0.08] flex items-center justify-center">
                    <row.icon size={15} className="text-zinc-400" aria-hidden="true" />
                  </div>
                  <span className="text-sm font-semibold text-white">{row.label}</span>
                </div>

                <div className="grid grid-cols-[1fr_auto_1fr] gap-4 items-center">
                  <div>
                    <div className="text-xs text-zinc-400 mb-2">{renderWithBrand(row.saife.text)}</div>
                    <div className="h-2 rounded-full bg-white/[0.05] overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${row.saife.bar}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: i * 0.1, ease: 'easeOut' }}
                        className={`h-full rounded-full bg-gradient-to-r ${row.saifeColor}`}
                      />
                    </div>
                  </div>

                  <div className="text-center px-2">
                    <div className="text-[10px] font-bold text-white/30 tracking-widest uppercase">{t('landing.why.vs')}</div>
                  </div>

                  <div>
                    <div className="text-xs text-zinc-600 mb-2 line-through decoration-zinc-600/40">{renderWithBrand(row.competitor.text)}</div>
                    <div className="h-2 rounded-full bg-white/[0.05] overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${row.competitor.bar}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: i * 0.1 + 0.2, ease: 'easeOut' }}
                        className={`h-full rounded-full bg-gradient-to-r ${row.compColor}`}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-between mt-2">
                  <span className={`text-[10px] font-semibold bg-gradient-to-r ${row.saifeColor} bg-clip-text text-transparent`} style={{ fontFamily: '"Liberation Serif", "Tinos", "Times New Roman", serif' }}>Heinrich</span>
                  <span className="text-[10px] text-zinc-600">{t('landing.why.competitor')}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <div className="h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" aria-hidden="true" />

      {/* E. Enterprise 로컬 LLM */}
      <section
        className="py-24 lg:py-32 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #030f0a 0%, #040d0f 50%, #05090f 100%)' }}
        aria-labelledby="enterprise-mini-title"
      >
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute top-1/4 right-0 w-[50rem] h-[50rem] bg-emerald-500/08 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 left-0 w-[40rem] h-[40rem] bg-teal-500/06 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div {...inView}>
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-emerald-500/15 border border-emerald-500/25 text-zinc-900 dark:text-white uppercase tracking-widest mb-6">
                {t('landing.enterprise.badge')}
              </span>
              <h2
                id="enterprise-mini-title"
                className="font-bold tracking-tight leading-tight mb-5"
                style={{ fontSize: 'clamp(1.75rem, 4.5vw, 3rem)' }}
              >
                <span className="text-white">{t('landing.enterprise.headline1')}</span>
                <br />
                <span className="text-white">
                  {t('landing.enterprise.headline2')}
                </span>
              </h2>
              <p className="text-[15px] leading-[1.7] text-zinc-400 mb-6">
                {t('landing.enterprise.description')}
              </p>
              <ul className="flex flex-col gap-3 mb-8">
                {[
                  { icon: FiShield, text: t('landing.enterprise.bullet1') },
                  { icon: FiCheck, text: t('landing.enterprise.bullet2') },
                  { icon: FiCamera, text: t('landing.enterprise.bullet3') },
                ].map((item) => (
                  <li key={item.text} className="flex items-start gap-3 text-sm text-zinc-300">
                    <item.icon size={14} className="text-emerald-400 shrink-0 mt-0.5" aria-hidden="true" />
                    <span>{item.text}</span>
                  </li>
                ))}
              </ul>
              <Link to="/on-premise">
                <Button variant="outline" size="md" rightIcon={<FiArrowRight aria-hidden="true" />}
                  className="border-emerald-500/30 text-zinc-900 dark:text-white hover:border-emerald-500/60 hover:text-zinc-900 dark:text-white hover:bg-emerald-500/10"
                >
                  {t('landing.enterprise.learnMore')}
                </Button>
              </Link>
            </motion.div>

            <motion.div {...stagger(1)} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                {
                  value: '109/120',
                  sub: t('landing.enterprise.card1Sub'),
                  label: t('landing.enterprise.card1Label'),
                  gradient: 'from-emerald-600/25 to-teal-600/15',
                  border: 'border-emerald-500/30',
                  textColor: 'text-emerald-400',
                },
                {
                  value: '78/100',
                  sub: t('landing.enterprise.card2Sub'),
                  label: t('landing.enterprise.card2Label'),
                  gradient: 'from-teal-600/25 to-amber-600/15',
                  border: 'border-teal-500/30',
                  textColor: 'text-teal-400',
                },
                {
                  value: t('landing.enterprise.card3Value'),
                  sub: t('landing.enterprise.card3Sub'),
                  label: t('landing.enterprise.card3Label'),
                  gradient: 'from-amber-600/20 to-indigo-600/10',
                  border: 'border-amber-500/25',
                  textColor: 'text-amber-400',
                },
                {
                  value: t('landing.enterprise.card4Value'),
                  sub: t('landing.enterprise.card4Sub'),
                  label: t('landing.enterprise.card4Label'),
                  gradient: 'from-violet-600/20 to-purple-600/10',
                  border: 'border-violet-500/25',
                  textColor: 'text-violet-400',
                },
              ].map((card, i) => {
                const hasHangul = /[\uac00-\ud7af]/.test(card.value)
                return (
                  <motion.div
                    key={card.label}
                    {...stagger(i + 2)}
                    className={`rounded-lg border ${card.border} bg-gradient-to-br ${card.gradient} p-6 hover:scale-[1.02] transition-transform duration-300 cursor-default`}
                  >
                    <div className={`tracking-tight whitespace-nowrap ${hasHangul ? '' : 'tabular-nums'} text-3xl font-bold ${card.textColor} mb-1`}>
                      {card.value}
                    </div>
                    <div className="text-xs text-zinc-500 mb-2">{card.sub}</div>
                    <div className="text-sm font-semibold text-white">{card.label}</div>
                  </motion.div>
                )
              })}
            </motion.div>
          </div>
        </div>
      </section>

      <div className="h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" aria-hidden="true" />

      {/* F. Interactive Demo */}
      <section
        className="py-24 lg:py-32 relative overflow-hidden bg-zinc-950"
        aria-labelledby="demo-title"
      >
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60rem] h-[40rem] bg-gradient-to-br from-amber-500/08 via-amber-500/05 to-violet-500/06 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <motion.div {...inView} className="text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-amber-500/15 border border-amber-500/25 text-zinc-900 dark:text-white uppercase tracking-widest mb-5">
              {t('landing.demo.badge')}
            </span>
            <h2
              id="demo-title"
              className="font-bold tracking-tight leading-tight mb-5"
              style={{ fontSize: 'clamp(1.75rem, 4.5vw, 3rem)' }}
            >
              <span className="text-white">{t('landing.demo.headline1')}</span>{' '}
              <span className="text-amber-300">
                {t('landing.demo.headline2')}
              </span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr_1fr] gap-6 items-start">
            <motion.div {...stagger(0)} className="hidden lg:flex flex-col gap-4 pt-8">
              {[
                { icon: FiCheck, color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20', title: t('landing.demo.card1Title'), desc: t('landing.demo.card1Desc') },
                { icon: FiZap, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20', title: t('landing.demo.card2Title'), desc: t('landing.demo.card2Desc') },
              ].map((c) => (
                <div key={c.title} className={`rounded-md border ${c.bg} p-4`}>
                  <div className="flex items-center gap-2 mb-2">
                    <c.icon size={14} className={c.color} aria-hidden="true" />
                    <span className="text-xs font-semibold text-white">{c.title}</span>
                  </div>
                  <p className="text-xs text-zinc-500">{c.desc}</p>
                </div>
              ))}
            </motion.div>

            <motion.div {...stagger(1)}>
              <DemoPreview />
            </motion.div>

            <motion.div {...stagger(2)} className="hidden lg:flex flex-col gap-4 pt-8">
              {[
                { icon: FiRefreshCw, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20', title: t('landing.demo.card3Title'), desc: t('landing.demo.card3Desc') },
                { icon: FiGlobe, color: 'text-violet-400', bg: 'bg-violet-500/10 border-violet-500/20', title: t('landing.demo.card4Title'), desc: t('landing.demo.card4Desc') },
              ].map((c) => (
                <div key={c.title} className={`rounded-md border ${c.bg} p-4`}>
                  <div className="flex items-center gap-2 mb-2">
                    <c.icon size={14} className={c.color} aria-hidden="true" />
                    <span className="text-xs font-semibold text-white">{c.title}</span>
                  </div>
                  <p className="text-xs text-zinc-500">{c.desc}</p>
                </div>
              ))}
            </motion.div>
          </div>

          <motion.div {...inView} className="text-center mt-10">
            <Link to="/signup">
              <Button size="md" rightIcon={<FiArrowRight aria-hidden="true" />}>
                {t('landing.demo.startFree')}
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <div className="h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" aria-hidden="true" />

      {/* G. 가격 미리보기 */}
      <section
        className="py-24 lg:py-32 bg-[#060a14] relative overflow-hidden"
        aria-labelledby="pricing-preview-title"
      >
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute top-0 right-0 w-[40rem] h-[30rem] bg-amber-500/06 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[35rem] h-[25rem] bg-violet-500/05 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <motion.div {...inView} className="text-center mb-16">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-amber-500/15 to-violet-500/10 border border-amber-500/25 text-amber-300 uppercase tracking-widest mb-5">
              {t('landing.pricingPreview.badge')}
            </span>
            <h2
              id="pricing-preview-title"
              className="font-bold tracking-tight leading-tight mb-5"
              style={{ fontSize: 'clamp(1.75rem, 4.5vw, 3rem)' }}
            >
              <span className="text-white">
                {t('landing.pricingPreview.headline')}
              </span>
            </h2>
            <p className="text-[15px] leading-[1.7] text-zinc-400">
              {t('landing.pricingPreview.description')}
            </p>
          </motion.div>

          <div className="flex flex-col md:flex-row gap-4 items-stretch justify-center max-w-4xl mx-auto md:items-end">
            {PRICE_PLANS.map((plan, i) => {
              const priceText = 'priceKey' in plan ? t(plan.priceKey!) : plan.price
              const period = 'priceKey' in plan ? plan.period : t('pricing.planSuffixMonthly')
              return (
              <motion.div
                key={plan.id}
                {...stagger(i)}
                className={`relative flex-1 ${plan.scale}`}
              >
                {plan.id === 'pro' && (
                  <div className="absolute -inset-px rounded-lg bg-gradient-to-br from-amber-500/50 to-amber-500/30 blur-sm" aria-hidden="true" />
                )}
                <div
                  className={`relative h-full rounded-lg border ${plan.border} bg-gradient-to-br ${plan.gradient} p-6 flex flex-col gap-4 backdrop-blur-sm`}
                  style={{ boxShadow: plan.id === 'pro' ? '0 0 40px rgba(59,130,246,0.15)' : undefined }}
                >
                  <div>
                    <div className="text-xs text-zinc-500 mb-1">{t(plan.descKey)}</div>
                    <div className="text-base font-semibold text-white">{plan.name}</div>
                    <div className="mt-3">
                      <span className="tracking-tight tabular-nums text-3xl font-bold text-white whitespace-nowrap">{priceText}</span>
                      {period && <span className="text-sm text-zinc-500 ml-1">{period}</span>}
                    </div>
                  </div>

                  <ul className="flex-1 flex flex-col gap-2.5">
                    {plan.featureKeys.map((fk) => (
                      <li key={fk} className="flex items-start gap-2 text-sm text-zinc-300">
                        <FiCheck size={12} className="text-amber-400 shrink-0 mt-0.5" aria-hidden="true" />
                        {t(fk)}
                      </li>
                    ))}
                  </ul>

                  <Link to={plan.href}>
                    <Button
                      variant={plan.id === 'pro' ? 'primary' : 'outline'}
                      size="sm"
                      className="w-full"
                      aria-label={t('landing.pricingPreview.planAria', { name: plan.name })}
                    >
                      {t(plan.ctaKey)}
                    </Button>
                  </Link>
                </div>
              </motion.div>
            )})}
          </div>

          <motion.div {...inView} className="text-center mt-12">
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors group"
            >
              {t('landing.pricingPreview.comparePlans')}
              <FiArrowRight size={14} className="group-hover:translate-x-1 transition-transform" aria-hidden="true" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* H. FAQ */}
      <section
        className="py-24 lg:py-32 bg-zinc-950"
        aria-labelledby="faq-title"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...inView} className="text-center mb-16">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-zinc-800/60 border border-white/[0.08] text-zinc-400 uppercase tracking-widest mb-5">
              FAQ
            </span>
            <h2
              id="faq-title"
              className="font-bold tracking-tight text-white"
              style={{ fontSize: 'clamp(1.75rem, 4vw, 2.75rem)' }}
            >
              {t('landing.faqTitle')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-0 max-w-5xl mx-auto">
            {faqs.map((faq, idx) => (
              <motion.div key={idx} {...stagger(idx % 3)} className="border-b border-white/[0.06] last:border-b-0">
                <button
                  className="w-full flex items-center justify-between gap-4 py-6 text-left hover:text-white transition-colors duration-200 group"
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  aria-expanded={openFaq === idx}
                  aria-controls={`faq-answer-${idx}`}
                >
                  <span className="font-medium text-white text-sm leading-relaxed group-hover:text-amber-200 transition-colors">
                    {renderWithBrand(faq.q)}
                  </span>
                  <motion.span
                    animate={{ rotate: openFaq === idx ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                    className="shrink-0 text-zinc-500 group-hover:text-amber-400 transition-colors"
                    aria-hidden="true"
                  >
                    <FiChevronDown size={16} />
                  </motion.span>
                </button>
                <AnimatePresence initial={false}>
                  {openFaq === idx && (
                    <motion.div
                      id={`faq-answer-${idx}`}
                      key="answer"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <div className="pb-6 text-[14px] text-zinc-400 leading-[1.75]">
                        {renderWithBrand(faq.a)}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* I. 최종 CTA */}
      <section
        className="relative py-32 overflow-hidden"
        aria-label={t('landing.finalCta.ariaLabel')}
      >
        <div className="absolute inset-0 bg-zinc-950" aria-hidden="true" />

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 text-center">
          <div>
            <h2
              className="font-bold tracking-tight mb-5 leading-tight text-white"
              style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)' }}
            >
              {t('landing.finalCta.headline1')}{' '}
              <span className="text-amber-400">
                {t('landing.finalCta.headline2')}
              </span>
            </h2>
            <p className="text-[15px] leading-[1.7] text-zinc-400 mb-10 max-w-xl mx-auto">
              {t('landing.finalCta.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/signup">
                <Button
                  size="lg"
                  rightIcon={<FiArrowRight aria-hidden="true" />}
                  className="text-base px-10 w-full sm:w-auto"
                >
                  {t('landing.finalCta.freeAccount')}
                </Button>
              </Link>
              <Link to="/pricing">
                <Button
                  variant="outline"
                  size="lg"
                  className="text-base w-full sm:w-auto border-white/[0.20] hover:border-white/[0.35]"
                >
                  {t('landing.finalCta.comparePlans')}
                </Button>
              </Link>
            </div>

            <p className="mt-8 text-xs text-zinc-600">
              {t('landing.finalCta.noCardNote')}
            </p>
          </div>
        </div>
      </section>

      {/* 예시 결과물 모달 — 신규 기능 카드 클릭 시 트리거 */}
      <ExamplePreviewModal
        featureKey={exampleKey ?? ''}
        isOpen={exampleKey !== null}
        onClose={() => setExampleKey(null)}
      />
    </>
  )
}

// ── 실제 분석 결과 탭 전환 UI ────────────────────────────────────
function ResultsTabSection() {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState(0)

  const TABS = [
    // 1. 안전교육 자료 — 사용자 요청으로 최상단
    {
      label: t('landing.results.safetyEducationLabel'),
      icon: FiBookOpen,
      image: '/assets/features/safety-education.png',
      title: t('landing.results.safetyEducationTitle'),
      desc: t('landing.results.safetyEducationDesc'),
      href: '/dashboard/safety-education',
      gradient: 'from-zinc-700/30 to-zinc-800/15',
      border: 'border-white/[0.10]',
      color: 'text-zinc-300',
      activeBg: 'bg-white/[0.08] border-white/[0.20]',
    },
    // 2. TBM 자료 — 두 번째 (이미지 new_tbm1.png 로 교체됨)
    {
      label: t('landing.results.tbmLabel'),
      icon: FiFileText,
      image: '/assets/results/tbm-result.png',
      title: t('landing.results.tbmTitle'),
      desc: t('landing.results.tbmDesc'),
      href: '/dashboard/tbm',
      gradient: 'from-zinc-700/30 to-zinc-800/15',
      border: 'border-white/[0.10]',
      color: 'text-zinc-300',
      activeBg: 'bg-white/[0.08] border-white/[0.20]',
    },
    {
      label: t('landing.results.imageAnalysisLabel'),
      icon: FiCamera,
      image: '/assets/results/image-analysis.png',
      title: t('landing.results.imageAnalysisTitle'),
      desc: t('landing.results.imageAnalysisDesc'),
      href: '/dashboard/image-analysis',
      gradient: 'from-zinc-700/30 to-zinc-800/15',
      border: 'border-white/[0.10]',
      color: 'text-zinc-300',
      activeBg: 'bg-white/[0.08] border-white/[0.20]',
    },
    {
      label: t('landing.results.jsaExcelLabel'),
      icon: FiClipboard,
      image: '/assets/results/jsa-excel.png',
      title: t('landing.results.jsaExcelTitle'),
      desc: t('landing.results.jsaExcelDesc'),
      href: '/dashboard/jsa',
      gradient: 'from-zinc-700/30 to-zinc-800/15',
      border: 'border-white/[0.10]',
      color: 'text-zinc-300',
      activeBg: 'bg-white/[0.08] border-white/[0.20]',
    },
    {
      label: t('landing.results.workPermitLabel'),
      icon: FiFileText,
      image: '/assets/features/work-permit.png',
      title: t('landing.results.workPermitTitle'),
      desc: t('landing.results.workPermitDesc'),
      href: '/dashboard/work-permit',
      gradient: 'from-zinc-700/30 to-zinc-800/15',
      border: 'border-white/[0.10]',
      color: 'text-zinc-300',
      activeBg: 'bg-white/[0.08] border-white/[0.20]',
    },
    {
      label: t('landing.results.accidentReportLabel'),
      icon: FiAlertTriangle,
      image: '/assets/features/accident-report.png',
      title: t('landing.results.accidentReportTitle'),
      desc: t('landing.results.accidentReportDesc'),
      href: '/dashboard/accident-report',
      gradient: 'from-zinc-700/30 to-zinc-800/15',
      border: 'border-white/[0.10]',
      color: 'text-zinc-300',
      activeBg: 'bg-white/[0.08] border-white/[0.20]',
    },
    {
      label: t('landing.results.riskAssessmentLabel'),
      icon: FiShield,
      image: '/assets/features/risk-assessment.png',
      title: t('landing.results.riskAssessmentTitle'),
      desc: t('landing.results.riskAssessmentDesc'),
      href: '/dashboard/risk-assessment',
      gradient: 'from-zinc-700/30 to-zinc-800/15',
      border: 'border-white/[0.10]',
      color: 'text-zinc-300',
      activeBg: 'bg-white/[0.08] border-white/[0.20]',
    },
  ]

  const active = TABS[activeTab]

  return (
    <section className="py-24 lg:py-32 bg-zinc-950 relative overflow-hidden" aria-labelledby="results-title">
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute top-1/2 right-0 w-[40rem] h-[40rem] bg-amber-500/05 rounded-full blur-3xl -translate-y-1/2" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <motion.div {...inView} className="text-center mb-14">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-amber-500/15 border border-amber-500/25 text-zinc-900 dark:text-white uppercase tracking-widest mb-5">
            {t('landing.results.badge')}
          </span>
          <h2
            id="results-title"
            className="font-bold tracking-tight leading-tight mb-5"
            style={{ fontSize: 'clamp(1.75rem, 4.5vw, 3rem)' }}
          >
            <span className="text-white">{t('landing.results.headline1')}</span>{' '}
            <span className="text-white">
              {t('landing.results.headline2')}
            </span>
          </h2>
        </motion.div>

        <motion.div {...inView} className="flex justify-center gap-2 mb-8 flex-wrap">
          {TABS.map((tab, i) => (
            <button
              key={tab.label}
              onClick={() => setActiveTab(i)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium border transition-all duration-200 ${
                activeTab === i
                  ? `${tab.activeBg} ${tab.color}`
                  : 'bg-white/[0.04] border-white/[0.08] text-zinc-400 hover:border-white/[0.14] hover:text-white'
              }`}
            >
              <tab.icon size={14} aria-hidden="true" />
              {tab.label}
            </button>
          ))}
        </motion.div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.35 }}
          >
            <Link to={active.href} className="block group">
              <div className={`relative rounded-lg border ${active.border} bg-gradient-to-br ${active.gradient} overflow-hidden`}>
                <div className="absolute top-4 left-4 z-10">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-semibold bg-black/50 backdrop-blur-sm border ${active.border} ${active.color}`}>
                    {t('landing.liveOutput')}
                  </span>
                </div>
                <img
                  src={active.image}
                  alt={t('landing.results.imageAlt', { label: active.label })}
                  className="w-full h-80 lg:h-[480px] object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/85 via-transparent to-transparent" aria-hidden="true" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-lg font-bold text-white mb-2">{active.title}</h3>
                  <p className="text-sm text-zinc-400 flex items-center gap-2">
                    {active.desc}
                    <FiArrowRight size={12} className={`${active.color} group-hover:translate-x-1 transition-transform`} aria-hidden="true" />
                  </p>
                </div>
              </div>
            </Link>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  )
}
